import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useLocations } from '../../../features/locations/use-locations'

const soon = ['Overview', 'Network', 'Admin']
const mono = { fontFamily: 'var(--v2-font-mono)' } as const

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd
      className="rounded-[3px] border border-[var(--color-border)] px-1 text-[10px] leading-[14px] text-[var(--color-muted)]"
      style={mono}
    >
      {children}
    </kbd>
  )
}

function PinGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      aria-hidden="true"
    >
      <path d="M8 14s4.5-3.8 4.5-7.2a4.5 4.5 0 1 0-9 0C3.5 10.2 8 14 8 14Z" />
      <circle cx="8" cy="6.8" r="1.6" />
    </svg>
  )
}

export function SignalShell({ children }: { children: ReactNode }) {
  const { data } = useLocations()
  const count = data?.length
  return (
    <div
      className="flex h-full bg-[var(--color-bg)] text-[13px] text-[var(--color-fg)]"
      style={{ fontFamily: 'var(--v2-font-sans)' }}
    >
      <aside className="flex w-[208px] shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex h-10 items-center gap-2 border-b border-[var(--color-border)] px-3">
          <span
            className="grid size-5 place-items-center rounded-[4px] text-[11px] font-semibold"
            style={{ background: 'var(--color-accent)', color: 'var(--color-accent-fg)', ...mono }}
          >
            S
          </span>
          <span className="text-[13px] font-semibold tracking-[-0.01em]">Sync</span>
          <span className="ml-auto text-[11px] text-[var(--color-muted)]" style={mono}>
            dtlr
          </span>
        </div>
        <nav className="flex flex-col gap-px p-1.5" aria-label="Primary">
          <Link
            to="/locations"
            className="group flex h-7 items-center gap-2 rounded-[4px] px-2 text-[13px] font-medium text-[var(--color-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)] focus-visible:outline-1 focus-visible:outline-[var(--color-accent)]"
            activeProps={{
              className: '!bg-[var(--v2-accent-soft)] !text-[var(--color-fg)]',
              'aria-current': 'page',
            }}
          >
            <PinGlyph />
            Locations
            {count !== undefined && (
              <span className="ml-auto text-[11px] text-[var(--color-muted)]" style={mono}>
                {count}
              </span>
            )}
          </Link>
          <div className="mt-3 mb-1 px-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)] opacity-70">
            Soon
          </div>
          {soon.map((label) => (
            <span
              key={label}
              aria-disabled="true"
              className="flex h-7 cursor-default items-center gap-2 rounded-[4px] px-2 text-[13px] text-[var(--color-muted)] opacity-50"
            >
              <span className="size-4 rounded-[3px] border border-dashed border-current opacity-60" />
              {label}
            </span>
          ))}
        </nav>
        <div className="mt-auto flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2 text-[11px] text-[var(--color-muted)]">
          <span>Signal</span>
          <span className="flex items-center gap-1">
            <Kbd>/</Kbd> filter
          </span>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-10 shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-[12px] text-[var(--color-muted)]">
          <span>DTLR</span>
          <span className="opacity-40">/</span>
          <span className="font-medium text-[var(--color-fg)]">Locations</span>
          <span className="ml-auto flex items-center gap-1.5">
            <Kbd>G</Kbd>
            <Kbd>L</Kbd>
            <span>go to locations</span>
          </span>
        </header>
        <main className="min-h-0 flex-1 overflow-hidden">{children}</main>
      </div>
    </div>
  )
}
